/**
 * Fast token validation cache to avoid unnecessary server calls
 */

interface TokenValidationCache {
  token: string;
  isValid: boolean;
  lastValidated: number;
  ttl: number; // Time to live in milliseconds
}

class TokenValidationManager {
  private cache: TokenValidationCache | null = null;
  private readonly CACHE_TTL = 2 * 60 * 1000; // 2 minutes cache
  private readonly MIN_CACHE_INTERVAL = 30 * 1000; // Don't validate more than once per 30s

  /**
   * Check if we can use cached validation result
   */
  isCacheValid(token: string): boolean {
    if (!this.cache || this.cache.token !== token) {
      return false;
    }

    const now = Date.now();
    const timeSinceValidation = now - this.cache.lastValidated;
    
    return timeSinceValidation < this.cache.ttl;
  }

  /**
   * Check if we should skip validation due to recent check
   */
  shouldSkipValidation(token: string): boolean {
    if (!this.cache || this.cache.token !== token) {
      return false;
    }

    const now = Date.now();
    const timeSinceValidation = now - this.cache.lastValidated;
    
    return timeSinceValidation < this.MIN_CACHE_INTERVAL;
  }

  /**
   * Get cached validation result if available and valid
   */
  getCachedResult(token: string): boolean | null {
    if (this.isCacheValid(token)) {
      console.log('⚡ Using cached token validation result');
      return this.cache!.isValid;
    }
    return null;
  }

  /**
   * Cache validation result
   */
  cacheResult(token: string, isValid: boolean): void {
    this.cache = {
      token,
      isValid,
      lastValidated: Date.now(),
      ttl: isValid ? this.CACHE_TTL : 10000 // Cache invalid results for shorter time
    };
  }

  /**
   * Clear cache (called on logout or token change)
   */
  clearCache(): void {
    this.cache = null;
  }

  /**
   * Get cache status for debugging
   */
  getCacheStatus(): { hasCache: boolean; age: number | null; isValid: boolean | null } {
    if (!this.cache) {
      return { hasCache: false, age: null, isValid: null };
    }

    return {
      hasCache: true,
      age: Date.now() - this.cache.lastValidated,
      isValid: this.cache.isValid
    };
  }
}

// Export singleton instance
export const tokenCache = new TokenValidationManager();